import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { OrderStagingService } from './order-staging.service';
import { extractApiErrorMessage } from '../shared/utils/extract-api-error-message.util';
import { WarehouseService } from '../warehouse/warehouse.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-order-places',
  templateUrl: './order-places.component.html',
  styles: [
    ':host{display:block} .places{padding:12px;border:1px solid #dbe3ee;border-radius:10px;margin:12px 0}.place{display:flex;flex-wrap:wrap;align-items:center;gap:6px;margin:8px 0}.place strong{font-size:1.2rem} .error{color:#b91c1c} small{display:block;color:#64748b}'
  ]
})
export class OrderPlacesComponent implements OnChanges {
  @ViewChild('returnScanInput') returnScanInput?: ElementRef<HTMLInputElement>;

  @Input() orderId?: number;

  @Output() changed = new EventEmitter<void>();

  state: any;

  choices: any[] = [];

  cellId: number | null = null;

  loading = false;

  error = '';

  returnContent: any;

  returnBarcode = '';

  returnQuantity = 1;

  returnSourceCellId?: number;

  returnProductScanned = false;

  readonly returnScanQueue: string[] = [];

  private drainingReturnScans = false;

  legacyReservation: any;

  legacyProductScanned = false;

  legacyBarcode = '';

  readonly legacyScanQueue: string[] = [];

  private drainingLegacyScans = false;

  constructor(
    private readonly staging: OrderStagingService,
    private readonly warehouse: WarehouseService
  ) {}

  ngOnChanges(): void {
    this.legacyScanQueue.length = 0;
    this.legacyReservation = undefined;
    this.legacyProductScanned = false;
    this.returnScanQueue.length = 0;
    this.returnContent = undefined;
    void this.reload();
  }

  async reload(): Promise<void> {
    if (!this.orderId) return;
    const orderId = this.orderId;
    this.loading = true;
    try {
      const state = await this.staging.order(orderId);
      if (this.orderId === orderId) this.state = state;
    } catch (error) {
      this.error = extractApiErrorMessage(
        (error as any)?.error || error,
        'Не удалось загрузить места заказа'
      );
    } finally {
      this.loading = false;
      void this.drainLegacyScanQueue();
      void this.drainReturnScanQueue();
    }
  }

  async choosePlace(): Promise<void> {
    if (!this.state || this.loading) return;
    await this.run(async () => {
      this.choices = await this.staging.cells(this.state.warehouse_id, this.orderId!);
    });
  }

  async assign(): Promise<void> {
    if (!this.cellId) return;
    await this.run(async () => {
      await this.staging.assign(this.orderId!, this.cellId!);
      this.choices = [];
      this.state = await this.staging.order(this.orderId!);
      this.changed.emit();
    });
  }

  async release(placement: any): Promise<void> {
    await this.run(async () => {
      this.state = await this.staging.command(
        `release-${placement.id}`,
        `/order-staging/placements/${placement.id}/release`,
        {}
      );
      this.changed.emit();
    });
  }

  async print(placement: any, medium: 'LABEL' | 'RECEIPT'): Promise<void> {
    await this.run(() => this.staging.print(this.orderId!, placement.id, medium));
  }

  quantity(placement: any): number {
    return (this.state?.contents || [])
      .filter((row: any) => row.placement_id === placement.id)
      .reduce((sum: number, row: any) => sum + Number(row.quantity || 0), 0);
  }

  get returns(): any[] {
    return (this.state?.contents || []).filter((row: any) => row.status === 'RETURN_REQUIRED');
  }

  get returnableContents(): any[] {
    return (this.state?.contents || []).filter((row: any) => row.status === 'AVAILABLE' && Number(row.quantity) > 0);
  }

  async requestReturn(content: any): Promise<void> {
    if (this.loading || this.pendingScope || !window.confirm(
      'Оприходование сохраняется. Будет создано задание: сканировать исходную ячейку, товар и место возврата. Вернуть товар на склад?'
    )) return;
    await this.run(async () => {
      this.state = await this.staging.command(
        `content-${content.id}-request-return`,
        `/order-staging/contents/${content.id}/request-return`,
        {}
      );
      this.changed.emit();
    });
  }

  beginLegacyPlace(reservation: any): void {
    this.legacyReservation = reservation;
    this.legacyProductScanned = false;
    this.legacyBarcode = '';
  }

  async scanLegacy(): Promise<void> {
    if (!this.legacyBarcode.trim()) return;
    const barcode = this.legacyBarcode.trim();
    this.legacyBarcode = '';
    this.legacyScanQueue.push(barcode);
    await this.drainLegacyScanQueue();
  }

  private async drainLegacyScanQueue(): Promise<void> {
    if (this.drainingLegacyScans || this.loading || this.pendingScope) return;
    this.drainingLegacyScans = true;
    try {
      while (this.legacyScanQueue.length && !this.loading && !this.pendingScope) {
        await this.processLegacyScan(this.legacyScanQueue.shift()!);
      }
    } finally {
      this.drainingLegacyScans = false;
    }
  }

  private async processLegacyScan(barcode: string): Promise<void> {
    await this.run(async () => {
      if (!this.legacyReservation)
        throw new Error('Выберите собранный товар перед следующим сканированием.');
      if (!this.legacyProductScanned) {
        const result = await firstValueFrom(
          this.warehouse.resolveBarcode({ barcode, warehouseId: this.state.warehouse_id })
        );
        if (
          result.type !== 'single_product_match' ||
          Number(result.product.id) !== Number(this.legacyReservation.product_id)
        ) {
          throw new Error('Скан не подтверждает выбранный товар. Проверьте упаковку и штрихкод.');
        }
        this.legacyProductScanned = true;
        return;
      }
      if (!barcode.startsWith('!'))
        throw new Error('Теперь отсканируйте ячейку размещения товара.');
      const code = barcode.slice(1).trim().toUpperCase();
      const cells = await firstValueFrom(
        this.warehouse.searchCells({ warehouseId: this.state.warehouse_id, code })
      );
      const cell = cells.find(
        (row) => row.code.toUpperCase() === code && row.isActive && row.type === 'REAL'
      );
      if (!cell) throw new Error('Активная физическая ячейка не найдена.');
      this.state = await this.staging.command(
        `order-${this.orderId}-legacy`,
        `/order-staging/orders/${this.orderId}/legacy-place`,
        {
          reservationId: this.legacyReservation.id,
          productId: this.legacyReservation.product_id,
          cellId: cell.id,
          quantity: 1
        }
      );
      this.legacyReservation = this.state.legacy_reservations?.find(
        (row: any) => row.id === this.legacyReservation.id
      );
      this.legacyProductScanned = false;
      this.changed.emit();
    });
  }

  cellCode(content: any): string {
    return (
      this.state?.placements?.find((place: any) => place.id === content.placement_id)?.cell_code ||
      '?'
    );
  }

  beginReturn(content: any): void {
    this.returnContent = content;
    this.returnBarcode = '';
    this.returnQuantity = 1;
    this.returnSourceCellId = undefined;
    this.returnProductScanned = false;
    this.focusReturnScan();
  }

  async confirmReturn(): Promise<void> {
    if (!this.returnBarcode.trim()) return;
    const barcode = this.returnBarcode.trim();
    this.returnBarcode = '';
    this.returnScanQueue.push(barcode);
    await this.drainReturnScanQueue();
  }

  private async drainReturnScanQueue(): Promise<void> {
    if (this.drainingReturnScans || this.loading || this.pendingScope) return;
    this.drainingReturnScans = true;
    try {
      while (this.returnScanQueue.length && !this.loading && !this.pendingScope)
        await this.processReturnScan(this.returnScanQueue.shift()!);
    } finally {
      this.drainingReturnScans = false;
    }
  }

  private async processReturnScan(barcode: string): Promise<void> {
    const content = this.returnContent;
    await this.run(async () => {
      if (!content) throw new Error('Выберите возвращаемый товар перед следующим сканированием.');
      if (this.returnSourceCellId && !this.returnProductScanned) {
        const product = await firstValueFrom(
          this.warehouse.resolveBarcode({ barcode, warehouseId: this.state.warehouse_id })
        );
        if (product.type !== 'single_product_match' || Number(product.product.id) !== Number(content.product_id))
          throw new Error('Отсканируйте выбранный товар из ячейки заказа.');
        this.returnProductScanned = true;
        return;
      }
      if (!barcode.startsWith('!')) throw new Error('Отсканируйте штрихкод ячейки !КОД.');
      const code = barcode.slice(1).trim().toUpperCase();
      const cells = await firstValueFrom(
        this.warehouse.searchCells({ warehouseId: this.state.warehouse_id, code })
      );
      const cell = cells.find((row) => row.code.toUpperCase() === code && row.isActive && row.type === 'REAL');
      if (!cell) throw new Error('Ячейка возврата не найдена на складе заказа.');
      if (!this.returnSourceCellId) {
        const source = this.state.placements?.find((place: any) => place.id === content.placement_id);
        if (Number(source?.cell_id) !== Number(cell.id))
          throw new Error(`Сначала отсканируйте исходную ячейку ${this.cellCode(content)}.`);
        this.returnSourceCellId = cell.id;
        return;
      }
      if (!Number.isFinite(Number(this.returnQuantity)) || this.returnQuantity <= 0 || this.returnQuantity > content.quantity)
        throw new Error('Проверьте количество возврата.');
      this.state = await this.staging.command(
        `order-${this.orderId}-return`,
        `/order-staging/contents/${content.id}/return`,
        {
          cellId: cell.id,
          sourceCellId: this.returnSourceCellId,
          quantity: Number(this.returnQuantity)
        }
      );
      this.returnContent = undefined;
      this.changed.emit();
    });
  }

  private focusReturnScan(): void {
    setTimeout(() => this.returnContent && this.returnScanInput?.nativeElement.focus());
  }

  get pendingScope(): string | undefined {
    return [
      `order-${this.orderId}-assign`,
      `order-${this.orderId}-return`,
      `order-${this.orderId}-legacy`,
      ...(this.state?.contents || []).map((content: any) => `content-${content.id}-request-return`),
      ...(this.state?.placements || []).map((place: any) => `release-${place.id}`)
    ].find((scope) => this.staging.hasPending(scope));
  }

  async retry(): Promise<void> {
    const scope = this.pendingScope;
    if (!scope) return;
    await this.run(async () => {
      await this.staging.retry(scope);
      this.returnContent = undefined;
      this.legacyReservation = undefined;
      this.state = await this.staging.order(this.orderId!);
      this.changed.emit();
    });
  }

  async run(action: () => Promise<any>): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    this.error = '';
    try {
      await action();
    } catch (error) {
      this.error = extractApiErrorMessage((error as any)?.error || error, 'Операция не выполнена');
    } finally {
      this.loading = false;
      void this.drainLegacyScanQueue();
      void this.drainReturnScanQueue();
      this.focusReturnScan();
    }
  }
}
